import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';

import Button from 'components/partials/button/button';
import type { ButtonVariant } from 'components/partials/button/button';
import ActionsDropdownItem from './actions-dropdown-item/actions-dropdown-item';
import { useActionsDropdownKeyboard } from './use-actions-dropdown-keyboard';

import { getTestId } from 'helpers/test-helpers';

import Icons from 'assets/icons';

const SCROLL_CLOSE_THRESHOLD_PX = 20;

interface MenuCoords {
  top: number;
  left: number;
  transform: string;
}

const getMenuPosition = (
  triggerEl: HTMLElement | null,
  menuPosition: string
): MenuCoords | null => {
  if (!triggerEl) return null;

  const rect = triggerEl.getBoundingClientRect();
  const top = rect.bottom + 4;

  if (menuPosition === 'left') {
    return { top, left: rect.left, transform: 'none' };
  }

  return { top, left: rect.right, transform: 'translateX(-100%)' };
};

export interface ActionsDropdownOption {
  label: string;
  startIcon?: React.ReactNode;
  isDelete?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  customClass?: string;
}

interface ActionsDropdownProps {
  buttonName?: string;
  options?: ActionsDropdownOption[];
  isDisabled?: boolean;
  iconBefore?: React.ReactNode;
  loading?: boolean;
  customClass?: string;
  startIcon?: React.ReactNode;
  /** Accessible name for icon-only triggers (defaults to "More actions"). */
  ariaLabel?: string;
  menuPosition?: 'left' | 'right';
  variant?: ButtonVariant;
  menuCustomClass?: string;
}

const ActionsDropdown = ({
  buttonName = '',
  options = [],
  isDisabled,
  iconBefore = null,
  loading = false,
  customClass = '',
  startIcon = null,
  ariaLabel,
  menuPosition = 'right',
  variant = 'reserve-outlined',
  menuCustomClass = '',
}: ActionsDropdownProps): React.JSX.Element => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownToggleBtn = useRef<HTMLButtonElement>(null);
  const initialTriggerTopRef = useRef<number | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);

  const { menuId, handleTriggerKeyDown, handleMenuKeyDown, closeMenu } =
    useActionsDropdownKeyboard({
      isOpen,
      setIsOpen,
      triggerRef: dropdownToggleBtn,
      menuRef: dropdownRef,
      isDisabled,
    });

  const dataTestId = getTestId({
    prefix: 'actions-dropdown',
    label: buttonName || 'button',
  });

  const updateMenuCoords = useCallback(() => {
    setMenuCoords(getMenuPosition(dropdownToggleBtn.current, menuPosition));
  }, [menuPosition]);

  useLayoutEffect(() => {
    if (isOpen) {
      const rect = dropdownToggleBtn.current?.getBoundingClientRect();

      if (rect) initialTriggerTopRef.current = rect.top;
      updateMenuCoords();
    } else {
      setMenuCoords(null);
      initialTriggerTopRef.current = null;
    }
  }, [isOpen, menuPosition, updateMenuCoords]);

  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener('scroll', updateMenuCoords, true);
    window.addEventListener('resize', updateMenuCoords);

    return () => {
      window.removeEventListener('scroll', updateMenuCoords, true);
      window.removeEventListener('resize', updateMenuCoords);
    };
  }, [isOpen, updateMenuCoords]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScrollClose = () => {
      const trigger = dropdownToggleBtn.current;
      const initialTop = initialTriggerTopRef.current;

      if (!trigger || initialTop == null) return;

      const currentTop = trigger.getBoundingClientRect().top;

      if (Math.abs(currentTop - initialTop) >= SCROLL_CLOSE_THRESHOLD_PX) {
        setIsOpen(false);
      }
    };

    document.addEventListener('scroll', handleScrollClose, true);

    return () =>
      document.removeEventListener('scroll', handleScrollClose, true);
  }, [isOpen]);

  const handleClickOutside = (e: MouseEvent): void => {
    const target = e.target as Node;

    if (
      !dropdownToggleBtn.current?.contains(target) &&
      !dropdownRef.current?.contains(target)
    ) {
      setIsOpen(false);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const menuContent = (
    <div
      id={menuId}
      role="menu"
      tabIndex={-1}
      className={twMerge(
        'z-[700] flex flex-col gap-1 w-fit border border-navy-200 bg-white rounded-md shadow-sm p-2',
        menuCustomClass
      )}
      ref={dropdownRef}
      onKeyDown={handleMenuKeyDown}
      style={
        menuCoords
          ? {
              position: 'fixed',
              top: menuCoords.top,
              left: menuCoords.left,
              transform: menuCoords.transform,
            }
          : undefined
      }
    >
      {options.map((option) => (
        <ActionsDropdownItem
          key={option.label}
          hideDropdownCallback={() => closeMenu(true)}
          {...option}
        />
      ))}
    </div>
  );

  return (
    <div className="relative inline-block">
      <Button
        ref={dropdownToggleBtn}
        className={twMerge(
          'gap-1.5 h-10 px-4 py-2',
          !buttonName && 'border-none',
          customClass
        )}
        onClick={handleClick}
        onKeyDown={handleTriggerKeyDown}
        disabled={isDisabled}
        iconBefore={iconBefore}
        loading={loading}
        variant={variant}
        type="button"
        data-testid={dataTestId}
        aria-label={buttonName ? undefined : ariaLabel ?? 'More actions'}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={isOpen ? menuId : undefined}
      >
        {!loading && startIcon}
        {buttonName && <span>{buttonName}</span>}
        {!!options.length && buttonName && (
          <Icons.Arrow
            customClass={classNames(
              'arrow-button transition shrink-0 rotate-90',
              {
                '!-rotate-90': isOpen,
              }
            )}
          />
        )}

        {!buttonName && !startIcon && <Icons.MoreVertical />}
      </Button>

      {isOpen && createPortal(menuContent, document.body)}
    </div>
  );
};

export default ActionsDropdown;
